import { useState, useMemo } from "react";
import { Search, Phone, Download } from "lucide-react";
import { Reveal } from "./Reveal";
import { CLINIC } from "@/data/content";
import { LAB_TESTS, LAB_FILTERS } from "@/data/labTests";

const PAGE = 24;

export default function Laborator() {
  const [filter, setFilter] = useState("toate");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);

  const activeFilter = LAB_FILTERS.find((f) => f.id === filter);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return LAB_TESTS.filter((t) => activeFilter.match(t.name) && (!q || t.name.toLowerCase().includes(q)));
  }, [filter, query, activeFilter]);

  const shown = results.slice(0, visible);

  const onFilter = (id) => { setFilter(id); setVisible(PAGE); };
  const onSearch = (v) => { setQuery(v); setVisible(PAGE); };

  return (
    <section id="laborator" className="py-20 lg:py-28 bg-[#FBF7EF]">
      <div className="max-w-7xl mx-auto px-6">
        <Reveal className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-widest text-[#A6823B]">Laborator de analize</span>
          <h2 className="font-display text-3xl lg:text-4xl font-bold text-[#2A2521] mt-3">
            Catalog analize & prețuri
          </h2>
          <p className="text-[#6B5C43] mt-4 text-lg">
            Peste {LAB_TESTS.length} de analize disponibile, cu prețuri transparente. Caută analiza dorită și sună pentru programarea recoltării.
          </p>
        </Reveal>

        <Reveal delay={0.04} className="mt-6">
          <a href={CLINIC.resultsUrl} target="_blank" rel="noopener noreferrer" data-testid="lab-results-online-banner"
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-[#2A2521] to-[#4a3a26] p-6 lg:p-7 group">
            <div className="flex items-center gap-4">
              <span className="w-12 h-12 rounded-xl bg-[#A6823B]/25 flex items-center justify-center shrink-0">
                <Download className="w-6 h-6 text-[#DCC79A]" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold text-white">Ai deja analizele făcute?</h3>
                <p className="text-white/70 text-sm mt-0.5">Descarcă-ți rezultatele online, rapid și securizat.</p>
              </div>
            </div>
            <span className="inline-flex items-center justify-center gap-2 bg-[#A6823B] group-hover:bg-[#8A6B2E] text-white font-semibold px-6 py-3 rounded-full transition-colors shrink-0">
              <Download className="w-4 h-4" /> Rezultate online
            </span>
          </a>
        </Reveal>

        {/* Search */}
        <Reveal delay={0.05} className="mt-8 max-w-xl">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A6823B]" />
            <input
              value={query}
              onChange={(e) => onSearch(e.target.value)}
              data-testid="lab-test-search-input"
              placeholder="Caută o analiză (ex: glicemie, TSH, vitamina D)"
              className="w-full pl-12 pr-4 py-4 rounded-full border border-[#EADFC6] bg-white focus:border-[#A6823B] focus:ring-4 focus:ring-[#A6823B]/10 outline-none text-[#2A2521] transition-all"
            />
          </div>
        </Reveal>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap gap-2">
          {LAB_FILTERS.map((f) => (
            <button key={f.id} onClick={() => onFilter(f.id)} data-testid={`lab-filter-${f.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                filter === f.id
                  ? "bg-[#A6823B] text-white shadow-md shadow-[#A6823B]/20"
                  : "bg-white border border-[#EADFC6] text-[#4A3F2E] hover:border-[#A6823B]/50"
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Results */}
        <div className="mt-8 bg-white rounded-2xl border border-[#EADFC6] p-4 sm:p-6">
          <div className="flex items-center justify-between mb-2 px-2">
            <span className="text-sm text-[#8A7A5E]" data-testid="lab-results-count">{results.length} analize</span>
          </div>
          {results.length === 0 ? (
            <p className="text-[#8A7A5E] p-4">Nu am găsit această analiză. Sună-ne și te ajutăm: <a className="text-[#A6823B] font-semibold" href={CLINIC.phoneRaw}>{CLINIC.phone}</a></p>
          ) : (
            <ul className="grid sm:grid-cols-2 gap-x-8" data-testid="lab-results-list">
              {shown.map((t, i) => (
                <li key={t.name + i} className="flex items-center justify-between gap-4 py-3 border-b border-[#F0E9D8]">
                  <span className="text-[#3D362B] text-sm sm:text-base">{t.name}</span>
                  <span className="font-semibold text-[#A6823B] shrink-0">{t.price}</span>
                </li>
              ))}
            </ul>
          )}

          {visible < results.length && (
            <div className="text-center mt-6">
              <button onClick={() => setVisible((v) => v + PAGE)} data-testid="lab-load-more"
                className="px-6 py-3 rounded-full bg-[#EFE7D6] hover:bg-[#A6823B] hover:text-white text-[#A6823B] font-semibold transition-colors">
                Afișează mai multe ({results.length - visible} rămase)
              </button>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 rounded-2xl bg-[#2A2521] p-6 lg:p-8">
          <div>
            <h3 className="font-display text-xl font-bold text-white">Nu găsești analiza dorită?</h3>
            <p className="text-white/70 text-sm mt-1">Efectuăm peste 500 de tipuri de analize. Sună-ne pentru orice analiză specializată.</p>
          </div>
          <a href={CLINIC.phoneRaw}
            className="inline-flex items-center gap-2 bg-[#A6823B] hover:bg-[#8A6B2E] text-white font-semibold px-6 py-3.5 rounded-full transition-colors shrink-0">
            <Phone className="w-4 h-4" /> {CLINIC.phone}
          </a>
        </div>
      </div>
    </section>
  );
}
